import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Zap, Globe, Lock, ArrowLeft, ExternalLink, RefreshCw, Maximize2, X } from 'lucide-react';
import LightRays from '../components/LightRays';
import ParticleField from '../components/ParticleField';
import Cursor from '../components/Cursor';
import MagneticButton from '../components/MagneticButton';

const PAY_URL = 'https://bitypay.com/paylink/bqm_GwXoNFEvCMM';

const bullets = [
  { icon: Lock,        title: 'Secure transaction',    text: 'SSL encrypted checkout by Bitypay.' },
  { icon: Globe,       title: 'Any currency',          text: 'Pay from anywhere in the world.' },
  { icon: Zap,         title: 'Instant confirmation',  text: 'Real-time webhook notification.' },
  { icon: ShieldCheck, title: 'You choose the amount', text: 'Enter the amount in the terminal.' },
];

const PayByBity = () => {
  const iframeRef = useRef(null);
  const [now, setNow] = useState(new Date());
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Detect if iframe fails to load within 6 seconds (likely blocked by X-Frame-Options)
  useEffect(() => {
    setIframeReady(false);
    setIframeBlocked(false);
    const timer = setTimeout(() => {
      // Use functional updater to read the *current* readiness state
      setIframeReady((ready) => {
        if (!ready) setIframeBlocked(true);
        return ready;
      });
    }, 6000);
    return () => clearTimeout(timer);
  }, [reloadKey]);

  const time = now.toLocaleTimeString('en-GB', { hour12: false });

  const reload = () => {
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden bg-black">
      <LightRays />
      <ParticleField />
      <Cursor />

      <div className="relative z-[3] min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-cursor="hover" className="group flex items-center gap-3">
            <div className="relative w-2 h-2 rounded-full bg-cyan-400 animate-pulseGlow" />
            <span className="text-sm tracking-[0.3em] uppercase font-medium text-white/90">OZZLABS</span>
            <span className="hidden sm:inline font-mono text-[10px] text-white/40">— PAYMENTS</span>
          </Link>

          <Link
            to="/"
            data-cursor="hover"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to site</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </header>

        {/* Chapter marker */}
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 mt-6 md:mt-8 animate-fadeInUp">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
            <span className="font-mono text-cyan-400">TX / 00</span>
            <span className="w-10 h-px bg-white/20" />
            <span>Chapter — Transaction</span>
          </div>
        </div>

        {/* Main */}
        <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 lg:px-10 py-8 md:py-12 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: title + info */}
          <div className="lg:col-span-5 order-1">
            <h1
              className="font-light tracking-[-0.04em] leading-[0.9] text-[46px] sm:text-[64px] md:text-[88px] lg:text-[92px] animate-fadeInUp"
              style={{ animationDelay: '150ms' }}
            >
              <span className="block chromatic-text">Pay by</span>
              <span className="block font-serif neon-text italic">Bity.</span>
            </h1>

            <p
              className="mt-6 max-w-lg text-[14px] md:text-[15px] text-white/60 leading-relaxed animate-fadeInUp"
              style={{ animationDelay: '350ms' }}
            >
              Send us any amount — for a service, an invoice, a deposit or a thank you.
              Enter the amount inside the secure terminal on the right.
            </p>

            <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-1 gap-x-8 gap-y-5 animate-fadeInUp" style={{ animationDelay: '500ms' }}>
              {bullets.map((b) => {
                const Icon = b.icon;
                return (
                  <li key={b.title} className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-cyan-300" strokeWidth={1.6} />
                    </span>
                    <div>
                      <div className="text-sm font-medium text-white">{b.title}</div>
                      <div className="mt-1 text-[13px] text-white/55">{b.text}</div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Meta */}
            <dl className="mt-10 pt-6 border-t border-white/10 grid grid-cols-2 gap-y-3 text-sm animate-fadeInUp" style={{ animationDelay: '650ms' }}>
              <dt className="text-white/45">Provider</dt>
              <dd className="text-right text-white/85">Bitypay</dd>
              <dt className="text-white/45">Reference</dt>
              <dd className="text-right text-white/85 font-mono text-xs truncate">bqm_GwXoNFEvCMM</dd>
              <dt className="text-white/45">Session</dt>
              <dd className="text-right text-white/85 font-mono text-xs">{time}</dd>
            </dl>
          </div>

          {/* Right: cinematic terminal frame with iframe */}
          <div className="lg:col-span-7 order-2 w-full animate-fadeInUp" style={{ animationDelay: '400ms' }}>
            <div className="relative rounded-2xl reflective overflow-hidden">
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

              {/* Terminal titlebar */}
              <div className="relative flex items-center justify-between px-5 md:px-6 h-12 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-blinkDot" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                    Terminal · secure
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    data-cursor="hover"
                    onClick={reload}
                    className="w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition"
                    aria-label="Reload terminal"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    data-cursor="hover"
                    onClick={() => setFullscreen(true)}
                    className="w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition hidden md:flex"
                    aria-label="Expand terminal"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={PAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cursor="hover"
                    className="w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition"
                    aria-label="Open in new tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Iframe container — responsive */}
              <div
                className="relative bg-black"
                style={{ height: 'clamp(560px, 82vh, 900px)' }}
              >
                {/* Loader */}
                {!iframeReady && !iframeBlocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 pointer-events-none">
                    <div className="relative w-14 h-14">
                      <span className="absolute inset-0 rounded-full border-2 border-white/10" />
                      <span className="absolute inset-0 rounded-full border-2 border-t-cyan-400 animate-spin" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                      Connecting to Bitypay…
                    </div>
                  </div>
                )}

                {/* Blocked fallback */}
                {iframeBlocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 z-10 p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div className="max-w-sm">
                      <h4 className="font-light text-2xl chromatic-text">
                        Secure checkout opens outside
                      </h4>
                      <p className="mt-3 text-sm text-white/55 leading-relaxed">
                        For your safety, Bitypay processes payments on their own domain.
                        Tap below to launch the secure terminal in a new tab.
                      </p>
                    </div>
                    <MagneticButton>
                      <a
                        href={PAY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-cursor="hover"
                        className="btn-primary inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full text-[13px] uppercase tracking-[0.22em]"
                      >
                        Launch secure terminal
                        <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center">
                          <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </a>
                    </MagneticButton>
                  </div>
                )}

                {/* Actual iframe */}
                <iframe
                  key={reloadKey}
                  ref={iframeRef}
                  title="Bitypay secure checkout"
                  src={PAY_URL}
                  onLoad={() => setIframeReady(true)}
                  className={`w-full h-full border-0 transition-opacity duration-500 ${iframeReady ? 'opacity-100' : 'opacity-0'}`}
                  allow="payment *; clipboard-read; clipboard-write"
                  referrerPolicy="no-referrer-when-downgrade"
                  sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
                />
              </div>

              {/* Terminal footer */}
              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-5 md:px-6 py-3 border-t border-white/10 bg-white/[0.02]">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/35 font-mono">
                  {time} · UTC · encrypted
                </span>
                <a
                  href={PAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-cyan-300 hover:text-white transition-colors"
                >
                  Trouble loading? Open in new tab
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            <p className="mt-4 text-[11px] leading-relaxed text-white/40 text-center md:text-left">
              You will be redirected to Bitypay&apos;s secure checkout inside the frame.
              Ozzlabs never sees or stores your card details.
            </p>
          </div>
        </main>

        {/* Footer bar */}
        <footer className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-white/35 font-mono">
          <span>© {new Date().getFullYear()} Ozzlabs · Payments</span>
          <span>Encrypted transmission · Bitypay v1</span>
        </footer>
      </div>

      {/* Fullscreen modal */}
      {fullscreen && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col p-4 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-blinkDot" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                Terminal · fullscreen
              </span>
            </div>
            <button
              type="button"
              data-cursor="hover"
              onClick={() => setFullscreen(false)}
              className="w-10 h-10 rounded-full border border-white/15 hover:border-cyan-400/60 flex items-center justify-center text-white transition"
              aria-label="Close fullscreen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 rounded-2xl reflective overflow-hidden">
            <iframe
              title="Bitypay secure checkout (fullscreen)"
              src={PAY_URL}
              className="w-full h-full border-0"
              allow="payment *; clipboard-read; clipboard-write"
              referrerPolicy="no-referrer-when-downgrade"
              sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PayByBity;
